import { useNavigate } from "react-router-dom";
import { NuplayWordmark } from "./NuplayWordmark";

/**
 * Shared vertical landing page.
 *
 * The first four landings were hand-written and are ~95% identical — the same hero,
 * the same three feature rows, the same demo card. Rather than paste a fifth and sixth
 * copy, the new verticals share this one component and differ only by config. Nothing
 * about the existing pages changes.
 */

export interface LandingFeature {
  gradient: string;   // tailwind "from-… to-…" pair
  icon: React.ReactNode;
  title: string;
  desc: string;
}

export interface LandingConfig {
  /** Deep link into the call surface, already carrying agent/persona/query. */
  startHref: string;
  mark: { initials: string; gradient: string };
  brandName: string;
  brandTagline: string;
  eyebrow: string;
  /** Headline is split so the second half can take the gradient treatment. */
  headline: string;
  headlineAccent: string;
  intro: React.ReactNode;
  features: LandingFeature[];
  /** The right-hand demo card. */
  card: {
    agentLine: string;
    blurb: React.ReactNode;
    rows: { label: string; value: string }[];
    footnote: string;
  };
  footerLine: string;
}

function BrandMark({ initials, gradient, size = 34 }: { initials: string; gradient: string; size?: number }) {
  return (
    <span
      className="flex items-center justify-center text-white"
      style={{
        width: size, height: size, borderRadius: 10, background: gradient,
        fontFamily: "Nunito, system-ui, sans-serif", fontWeight: 800, fontSize: size * 0.4, letterSpacing: 0.3,
      }}
    >
      {initials}
    </span>
  );
}

export function VerticalLanding({ config }: { config: LandingConfig }) {
  const navigate = useNavigate();
  const { mark, card } = config;

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--nx-bg)", fontFamily: "Nunito, system-ui, sans-serif" }}>
      {/* ── Background atmosphere ── */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="orb animate-orb_float" style={{ width: 700, height: 700, top: "-15%", right: "-10%", background: "radial-gradient(circle, rgba(37,130,235,0.13) 0%, transparent 70%)" }} />
        <div className="orb" style={{ width: 500, height: 500, bottom: "5%", left: "-8%", background: "radial-gradient(circle, rgba(37,130,235,0.09) 0%, transparent 70%)", animationDelay: "3s" }} />
        <div className="absolute inset-0 opacity-[0.018]" style={{ backgroundImage: "radial-gradient(circle, white 1px, transparent 1px)", backgroundSize: "24px 24px" }} />
      </div>

      {/* ── Nav ── */}
      <nav className="sticky top-0 z-30 flex-shrink-0" style={{ background: "var(--nx-nav)", backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)", borderBottom: "1px solid var(--nx-border)" }}>
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <BrandMark initials={mark.initials} gradient={mark.gradient} />
            <div className="flex flex-col leading-none">
              <span className="text-[15px] font-bold tracking-tight" style={{ color: "var(--nx-text-1)" }}>{config.brandName}</span>
              <span className="text-[9px] font-semibold tracking-[0.16em] uppercase" style={{ color: "var(--nx-text-3)" }}>{config.brandTagline}</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden sm:flex items-center gap-2">
              <span className="text-[11px] font-medium" style={{ color: "var(--nx-text-3)" }}>Powered by</span>
              <span style={{ color: "var(--nx-text-1)", display: "inline-flex" }}><NuplayWordmark height={15} /></span>
            </div>
            <span className="hidden sm:block w-px h-5" style={{ background: "var(--nx-border)" }} />
            <button
              onClick={() => navigate(config.startHref)}
              className="flex items-center gap-2 px-4 py-1.5 rounded-full text-[12px] font-medium transition-all duration-150 hover:scale-105"
              style={{ background: "rgba(52,211,153,0.10)", border: "1px solid rgba(52,211,153,0.20)", color: "var(--color-success)" }}
            >
              <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "var(--color-success)" }} />
              AI Agent Online
            </button>
          </div>
        </div>
      </nav>

      {/* ── Hero ── */}
      <div className="relative flex-1">
        <div className="relative max-w-6xl mx-auto px-6 pt-14 pb-16 md:pt-20 md:pb-20 grid md:grid-cols-[1fr_420px] gap-14 items-start">
          {/* Left — copy */}
          <div className="step-enter">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-semibold tracking-[0.09em] uppercase mb-8" style={{ background: "rgba(37,130,235,0.12)", border: "1px solid rgba(37,130,235,0.25)", color: "var(--nx-accent-text)" }}>
              <span className="w-1 h-1 rounded-full" style={{ background: "var(--nx-accent-text)" }} />
              {config.eyebrow}
            </div>
            <h1 className="text-[42px] md:text-[52px] font-extrabold leading-[1.08] tracking-[-0.035em] mb-6" style={{ color: "var(--nx-text-1)" }}>
              {config.headline}{" "}
              <span style={{ background: "linear-gradient(135deg, #60BDFA 0%, #2582EB 50%, #1D4885 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                {config.headlineAccent}
              </span>
            </h1>
            <p className="text-[16px] leading-relaxed mb-12 max-w-lg" style={{ color: "var(--nx-text-2)" }}>
              {config.intro}
            </p>
            <div className="space-y-6">
              {config.features.map(({ gradient, icon, title, desc }) => (
                <div key={title} className="flex items-start gap-4">
                  <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center flex-shrink-0 mt-0.5 shadow-lg`}>{icon}</div>
                  <div>
                    <div className="text-[14px] font-semibold mb-0.5" style={{ color: "var(--nx-text-1)" }}>{title}</div>
                    <div className="text-[13px] leading-relaxed" style={{ color: "var(--nx-text-2)" }}>{desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — demo card */}
          <div className="step-enter" style={{ animationDelay: "60ms" }}>
            <div className="rounded-2xl overflow-hidden" style={{ background: "var(--nx-card-bg)", border: "1px solid var(--nx-card-border)", boxShadow: "0 24px 64px rgba(0,0,0,0.4), 0 0 0 1px var(--nx-border)", backdropFilter: "blur(20px)" }}>
              <div className="h-[3px]" style={{ background: "linear-gradient(90deg, #2582EB 0%, #60BDFA 50%, #34d399 100%)" }} />
              <div className="px-7 pt-7 pb-8">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "linear-gradient(135deg, #2582EB, #1D6BD8)" }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z" /><path d="M19 10v2a7 7 0 0 1-14 0v-2" /><line x1="12" y1="19" x2="12" y2="22" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-[15px] font-bold" style={{ color: "var(--nx-text-1)" }}>{card.agentLine}</div>
                    <div className="text-[12px]" style={{ color: "var(--nx-text-3)" }}>{config.brandName}</div>
                  </div>
                </div>
                <p className="text-[13px] leading-relaxed mb-6" style={{ color: "var(--nx-text-2)" }}>{card.blurb}</p>
                <div className="rounded-xl p-4 mb-7" style={{ background: "var(--nx-hover)", border: "1px solid var(--nx-border)" }}>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.09em] mb-2.5" style={{ color: "var(--nx-text-3)" }}>Test customer</div>
                  {card.rows.map((r, i) => (
                    <div key={r.label} className={`flex items-center justify-between text-[12.5px] ${i ? "mt-1.5" : ""}`} style={{ color: "var(--nx-text-2)" }}>
                      <span>{r.label}</span><span className="font-semibold" style={{ color: "var(--nx-text-1)" }}>{r.value}</span>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => navigate(config.startHref)}
                  className="w-full h-12 rounded-xl flex items-center justify-center gap-2.5 text-[14px] font-semibold text-white transition-all duration-150 active:scale-[0.985] relative overflow-hidden"
                  style={{ background: "linear-gradient(135deg, #2582EB 0%, #1D6BD8 100%)", boxShadow: "0 4px 24px rgba(37,130,235,0.4)" }}
                >
                  Start the Demo
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </button>
                <p className="text-center text-[11px] mt-3.5" style={{ color: "var(--nx-text-3)" }}>{card.footnote}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Footer ── */}
      <footer style={{ borderTop: "1px solid var(--nx-border)" }}>
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <BrandMark initials={mark.initials} gradient={mark.gradient} size={22} />
            <span className="text-[13px]" style={{ color: "var(--nx-text-3)" }}>{config.footerLine}</span>
          </div>
          <span className="flex items-center gap-2 text-[13px]" style={{ color: "var(--nx-text-3)" }}>
            Powered by
            <span style={{ color: "var(--nx-text-1)", display: "inline-flex" }}><NuplayWordmark height={16} /></span>
          </span>
        </div>
      </footer>
    </div>
  );
}

export default VerticalLanding;
